import React, { useContext, useEffect, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { camelizeKeys } from 'humps';

import { Map } from '../../components/Map/Map';
import { RouteInfo } from '../../components/Route/RouteInfo';
import { RouteTools } from '../../components/Route/RouteTools';
import { RoutePrintTools } from '../../components/Route/RoutePrintTools';
import { RouteLoading } from '../../components/Route/RouteLoading';
import { TimeLine } from '../../components/TimeLine/TimeLine';
import { Card } from '../../components/Card/Card';
import { useMapApi } from './../../hooks/useMapApi';
import { countedEndPointDistance } from '../../utils/routeEndPoint';
import { LoadingContext } from '../../components/Loading/Loading';
import { RouteDelayModal } from '../../components/Route/RouteDelayModal';
import { VolumeInfo } from '../../components/Route/VolumeInfo';
import { MapStatsOverlay } from '../../components/Route/MapStatsOverlay';
import { ConfirmModal } from '../../components/Modal/ConfirmModal';
import { OrderType, isReturnIssueRoute } from '../../utils/utils';

export const ShowRouteContainer = () => {
  // get data from window object
  const route = camelizeKeys(window.routeData.route);
  const statuses = camelizeKeys(window.routeData.statuses);
  const isAdmin = camelizeKeys(window.routeData.is_admin);
  const orderMissingServicesPrices = camelizeKeys(window.routeData.order_missing_services_prices);
  const routeLoadingImages = camelizeKeys(window.routeData.route_loading_images);
  const { startPoint, endPoint, startTime, endPointVia, endPointDistance, endPointArrivalDatetime } = route;
  const setIsLoading = useContext(LoadingContext);
  const [api, setApi] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [renderMap, setRenderMap] = useState(true);
  const [routeResult, setRouteResult] = useState(null);
  const [isRouteCalculating, setIsRouteCalculating] = useState(false);
  const [routeOrders, setRouteOrders] = useState([]);
  const [areRouteOrdersLoaded, setAreRouteOrdersLoaded] = useState(false);

  useEffect(() => {
    const initGoogleServices = () => {
      if (window.google && window.google.maps) {
        setApi({ maps: window.google.maps, map: null });
      }
    };

    if (window.google && window.google.maps) {
      initGoogleServices();
    } else {
      const interval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(interval);
          initGoogleServices();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const {
    confirmedVolumeM3,
    totalVolumeM3,
    confirmedIncomePrice,
    totalIncomePrice,
    isNotPricedPackages,
    orderConfirmedIncomePrice,
    orderTotalIncomePrice,
    exchangeConfirmedIncomePrice,
    exchangeTotalIncomePrice
  } = useMemo(() => {
    return (routeOrders ?? []).reduce(
      (acc, { order }) => {
        const v = Number(order?.packagesVolumeM3) || 0;

        if (order?.type === 'order') {
          acc.totalVolumeM3 += v;
        }

        acc.totalIncomePrice += Number(order?.incomePrice?.price?.amount ?? 0);
        acc.orderTotalIncomePrice += Number(order?.incomePrice?.delivery?.amount ?? 0);
        acc.exchangeTotalIncomePrice += Number(order?.incomePrice?.return?.amount ?? 0);

        if (order?.incomePrice?.notPricedPackages?.length > 0) {
          acc.isNotPricedPackages = true;
        }

        if (order?.deliveryConfirmations?.filter((confirmation) => confirmation.routeId === route.id)?.[0]?.status === "confirmed") {
          if (order?.type === 'order') {
            acc.confirmedVolumeM3 += v;
          }

          acc.confirmedIncomePrice += Number(order?.incomePrice?.price?.amount ?? 0);
          acc.orderConfirmedIncomePrice += Number(order?.incomePrice?.delivery?.amount ?? 0);
          acc.exchangeConfirmedIncomePrice += Number(order?.incomePrice?.return?.amount ?? 0);
        }

        return acc;
      },
      {
        confirmedVolumeM3: 0,
        totalVolumeM3: 0,
        confirmedIncomePrice: 0,
        totalIncomePrice: 0,
        isNotPricedPackages: false,
        orderConfirmedIncomePrice: 0,
        orderTotalIncomePrice: 0,
        exchangeConfirmedIncomePrice: 0,
        exchangeTotalIncomePrice: 0
      }
    );
  }, [JSON.stringify(routeOrders)]);

  const hasReturnPicking = isReturnIssueRoute(route);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);

  // Keeps parity with the PDF list: no end point, no return leg.
  const returnLegDistance = countedEndPointDistance(endPoint, endPointDistance);

const distance = routeOrders
  .map(({ distance }) => Number(distance) || 0)
  .reduce((acc, d) => acc + d, returnLegDistance);

  const routingEngine = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('type') === 'osrm') return 'osrm';
    return 'google';
  }, []);

  const { renderStagesOnMap, startPointLocation, endPointLocation, routePolylines, roadSnappedPositions, routeDistance, routeDuration } = useMapApi({
    routingEngine,
    api,
    routeOrders,
    hideInactiveRouteOrders: false,
    startPoint: {
      address: startPoint,
    },
    endPoint: {
      address: endPoint,
      via: JSON.parse(endPointVia),
    },
    editable: false,
    onFinish: () => {
      setIsLoading(false);
    },
  });

  useEffect(() => {
    const ro = sortBy(
      Object.values(camelizeKeys(window.routeData.route_orders)).map((routeOrder) => {
        return {
          ...routeOrder,
          products: routeOrder.order?.orderProducts
            ? routeOrder.order.orderProducts.map((p) => {
              const productRouteOrder = p.routeOrders.find(({ routeOrderId }) => routeOrderId === routeOrder.id);

              return {
                ...p,
                disabledBy: !productRouteOrder && p.routeOrders?.length > 0 ? p.routeOrders[0] : null,
                disabled: !productRouteOrder && p.routeOrders?.length > 0,
                value: !!productRouteOrder,
              };
            })
            : [],
          via: routeOrder.via ? JSON.parse(routeOrder.via) : [],
          arrivalDatetime: new Date(routeOrder.arrivalDatetime),
          dailyBreakMinutes: Number(routeOrder.dailyBreakMinutes ?? 0),
        };
      }),
      ['stage', 'ordering']
    );

    setRouteOrders(ro);
    setAreRouteOrdersLoaded(true);
  }, []);

  useEffect(() => {
    if (!renderMap) return;
    if (!areRouteOrdersLoaded) return;
    if (!isMapReady) return;
    if (routingEngine === 'google' && !api) return;

    if (!routeOrders.length) {
      setIsLoading(false);
      setRenderMap(false);
      return;
    }

    setIsRouteCalculating(true);
    renderStagesOnMap().then((nextRouteResult) => {
      if (nextRouteResult) {
        setRouteResult(nextRouteResult);
      }
    }).finally(() => {
      setIsRouteCalculating(false);
    });
    setRenderMap(false);
  }, [routeOrders, areRouteOrdersLoaded, api, routingEngine, isMapReady]);

  const handleModalClose = () => setIsModalOpen(false);
  const handleModalOpen = () => setIsModalOpen(true);

  const openSettleModal = () => setIsSettleModalOpen(true);
  const closeSettleModal = () => setIsSettleModalOpen(false);

  const handleSettleConfirm = () => {
    closeSettleModal();

    window.location.href = `/admin/routes/${route.id}/settles`;
  }

  const handleRemoveOrderFromRoute = async (orderId) => {
    try {
      const response = await fetch(`/admin/routes/${route.id}/remove-order/${orderId}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
        },
      });

      if (!response.ok) {
        toastr.error(__('routes.something_went_wrong'));
        return;
      }

      setRouteOrders(prev => prev.filter(ro => ro.orderId !== orderId));
      toastr.success(__('order_returns.messages.order_removed_from_route_success'));
    } catch (error) {
      toastr.error(__('routes.something_went_wrong'));
    }
  };

  return (
    <>
      <Card
        title={route.routeNumber ?? ''}
        info={
          !route.settledAt && isAdmin && (
            <button type="button" className="btn btn-success" onClick={openSettleModal}>
              { __('routes.settled_modal') }
            </button>
          )
        }
        className='pt-2'
      >
        <Map
          routeOrders={routeOrders}
          startPointLocation={startPointLocation?.point}
          endPointLocation={endPointLocation?.point}
          routeId={route.id}
          isLoading={isRouteCalculating}
          routePolylines={routePolylines}
          roadSnappedPositions={roadSnappedPositions}
          fitRouteBoundsOnRender={Boolean(routeResult)}
          onMapLoad={() => setIsMapReady(true)}
          hasReturnPicking={hasReturnPicking}
        >
          <MapStatsOverlay
            hasReturnPicking={hasReturnPicking}
            confirmedVolumeM3={confirmedVolumeM3}
            totalVolumeM3={totalVolumeM3}
            car={route.car}
            confirmedIncomePrice={confirmedIncomePrice}
            totalIncomePrice={totalIncomePrice}
            isNotPricedPackages={isNotPricedPackages}
            orderConfirmedIncomePrice={orderConfirmedIncomePrice}
            orderTotalIncomePrice={orderTotalIncomePrice}
            exchangeConfirmedIncomePrice={exchangeConfirmedIncomePrice}
            exchangeTotalIncomePrice={exchangeTotalIncomePrice}
            distance={distance}
            routeDistance={routeDistance}
            routeDuration={routeDuration}
          />
        </Map>
      </Card>

      <RouteInfo route={route} routeOrders={routeOrders} />

      <Card title={__('volumes.volumes')}>
        <VolumeInfo
          confirmedVolumeM3={confirmedVolumeM3}
          totalVolumeM3={totalVolumeM3}
          car={route?.car}
        />
      </Card>

      {isAdmin ? (
        <RouteTools route={route} hasReturnPicking={hasReturnPicking} onOpenRouteDelayModal={handleModalOpen} />
      ) : (
        <RoutePrintTools routeId={route.id} />
      )}


      <RouteLoading images={routeLoadingImages} />

      <Card title={__('routes.route')}>
        <TimeLine
          editable={false}
          routeOrders={routeOrders}
          startTime={startTime}
          endPoint={{
            address: endPoint,
            arrivalDatetime: new Date(endPointArrivalDatetime),
            distance: returnLegDistance,
          }}
          distanceSum={distance}
          statuses={statuses}
          isAdmin={isAdmin}
          orderMissingServicesPrices={orderMissingServicesPrices}
          setRouteOrders={setRouteOrders}
          routeId={route.id}
          hasReturnPicking={hasReturnPicking}
          onRemoveOrder={handleRemoveOrderFromRoute}
        />
      </Card>

      <ConfirmModal
        isOpen={isSettleModalOpen}
        title={ __('routes.settled_modal') }
        message={ __('routes.settled_modal_description') }
        onCancel={closeSettleModal}
        onConfirm={handleSettleConfirm}
      />

      <RouteDelayModal isOpen={isModalOpen} onModalClose={handleModalClose} route={route} routeOrders={routeOrders} />
    </>
  );
};
